import { format, parse } from 'date-fns';
import React, { useEffect, useRef } from 'react';
import { useIsFetching, useQuery } from 'react-query';
import { InputElement } from 'components/ui/Form/Input';
import { Option, Select } from 'components/ui/Form/Select';
import { dateFormats } from 'utils/Dates';
import { FETCH_RUNPAYROLL_DATA } from 'components/RunPayroll/utils/queries';
import { Box } from '@razorpay/blade/components';
import api from 'api';
import { queryKeys } from './data';

export const MonthSelector = ({
  label,
  value,
  onChange,
  selectedMonths,
  isDisabled,
  orgId,
  userId,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  selectedMonths: string[];
  isDisabled: boolean;
  orgId: string;
  userId: string;
}) => {
  const dropdownRef = useRef<HTMLSelectElement>(null);
  const isFetchingData = useIsFetching(FETCH_RUNPAYROLL_DATA);

  const {
    data: payrollMonthsData,
    isLoading: isLoadingMonths,
    error: errorFetchingMonths,
  } = useQuery({
    queryKey: [queryKeys.GET_USER_PAYROLL_MONTHS, orgId, userId],
    queryFn: () =>
      api.arrears.queries.getUserPayrollMonthsByFilter({
        merchantId: orgId,
        tenantId: orgId,
        userCriteria: [
          {
            userId,
          },
        ],
        filters: {
          status: 'status_paid',
          getMonthsWithLopOnly: false,
        },
      }),
    enabled: !!orgId && !!userId,
  });

  const payrollMonths:string[] =
    payrollMonthsData?.payrollGetUserPayrollMonthsByFilter?.data?.userPayrollMonths?.[0]
      ?.payrollMonths || [];

  const hasError =
    Boolean(errorFetchingMonths) ||
    payrollMonthsData?.payrollGetUserPayrollMonthsByFilter.success === false;

  const monthList = payrollMonths
    .map((monthStr) => parse(monthStr, dateFormats.yearMonth, new Date()))
    .sort((a, b) => b.getTime() - a.getTime());

  const onSelectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    onChange(selectedValue);
  };

  useEffect(() => {
    if (!value) {
      if (dropdownRef.current) {
        dropdownRef.current.focus();
      }
    }
  }, []);

  const isDisabledState = !!isFetchingData || isDisabled || isLoadingMonths;

  return (
    <Box>
      <InputElement
        required
        label={label ? label : 'Select Payroll Month'}
        className={'payroll-month-selector-wrapper'}>
        <Select
          ref={dropdownRef}
          name="pm"
          onChange={onSelectionChange}
          value={value}
          disabled={isDisabledState}
          className="month-selector"
          aria-label="Select Payroll Month">
          {value === '' && <Option value="">Select Month</Option>}
          {monthList.map((month) => (
            <Option
              key={month.toDateString()}
              value={format(month, dateFormats.yearMonth)}
              data-date={month}
              disabled={selectedMonths.includes(format(month, dateFormats.yearMonth))}>
              {format(month, dateFormats.monthYear)}
            </Option>
          ))}
        </Select>
      </InputElement>
    </Box>
  );
};
